import React, { useEffect, useMemo } from "react";
import { Alert, Badge, Button, Label } from "reactstrap";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import CommonForm from "Components/Common/CommonForm";
import SectionCard from "Components/Common/SectionCard";
import styles from "../css/dispensing.module.scss";
import { AddDispensingDetailsProps, Step2FormValues } from "../helpers/dispensing.types";
import { getDosingQuantityFields } from "../helpers/dispensing.constant";
import { DISPENSING_LOG_PATHS } from "Routes/paths";
import { showToastMessage } from "common/service/CommonService";

const AddDispensingDetails: React.FC<AddDispensingDetailsProps> = ({
  step1Summary,
  editId = null,
  selectedRecord = null,
  onPrevious,
  onCancel,
  isSubmitting = false,
}) => {
  /* ------------------------------------------------------------------------ */
  /* 1. ROUTING & STATE                                                       */
  /* ------------------------------------------------------------------------ */
  const navigate = useNavigate();

  /* ------------------------------------------------------------------------ */
  /* 2. REACT HOOK FORM SETUP                                                 */
  /* ------------------------------------------------------------------------ */
  const {
    control,
    watch,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<Step2FormValues>({
    defaultValues: {
      subjectno: [],
      persubjectqty: step1Summary?.perSubjectQty ?? selectedRecord?.quantitypersubject ?? null,
      extraquantity: null,
      totalqtyneeded: selectedRecord?.totalquantityneeded ?? null,
    },
    mode: "onChange",
  });

  /* ------------------------------------------------------------------------ */
  /* 3. FORM WATCHERS                                                         */
  /* ------------------------------------------------------------------------ */
  const selectedSubjectNos = watch("subjectno") || [];
  const perSubjectQtyVal = watch("persubjectqty");
  const extraQuantityVal = watch("extraquantity");
  const totalQtyNeeded = watch("totalqtyneeded") || 0;

  /* ------------------------------------------------------------------------ */
  /* 4. MEMOIZED COMPUTATIONS (useMemo)                                       */
  /* ------------------------------------------------------------------------ */
  const subjectList = useMemo(() => {
    if (step1Summary?.subjectList && Array.isArray(step1Summary.subjectList) && step1Summary.subjectList.length > 0) {
      return step1Summary.subjectList.map((s) => String(s));
    }
    return Array.from({ length: 25 }, (_, i) => String(i + 1).padStart(2, "0"));
  }, [step1Summary?.subjectList]);

  const selectedContainers = step1Summary?.selectedContainers || [];
  const netUsableContainerQuantity = step1Summary?.netUsableContainerQuantity || 0;

  /* ------------------------------------------------------------------------ */
  /* 5. EFFECTS (useEffect) & COMPUTATIONS                                    */
  /* ------------------------------------------------------------------------ */

  // Populate per subject qty from step1Summary or selectedRecord
  useEffect(() => {
    if (step1Summary?.perSubjectQty !== undefined && step1Summary?.perSubjectQty !== null) {
      setValue("persubjectqty", step1Summary.perSubjectQty, { shouldValidate: true });
    } else if (selectedRecord?.quantitypersubject) {
      setValue("persubjectqty", selectedRecord.quantitypersubject, { shouldValidate: true });
    }
  }, [step1Summary?.perSubjectQty, selectedRecord?.quantitypersubject, setValue]);

  // Calculate total quantity needed
  useEffect(() => {
    const subCount = selectedSubjectNos.length;
    const perSubQty = Number(perSubjectQtyVal) || 0;
    const extraQty = Number(extraQuantityVal) || 0;
    const total = subCount * perSubQty + extraQty;
    setValue("totalqtyneeded", total > 0 ? total : null);
  }, [selectedSubjectNos, perSubjectQtyVal, extraQuantityVal, setValue]);

  /* ------------------------------------------------------------------------ */
  /* 6. EVENT HANDLERS                                                        */
  /* ------------------------------------------------------------------------ */
  const onFinalSubmit = (_data: Step2FormValues) => {
    showToastMessage("success", "Dispensing Log completed successfully!");
    navigate(DISPENSING_LOG_PATHS.LIST);
  };

  /* ------------------------------------------------------------------------ */
  /* 7. RENDER COMPONENT                                                      */
  /* ------------------------------------------------------------------------ */
  return (
    <form onSubmit={handleSubmit(onFinalSubmit)}>
      <SectionCard
        index={1}
        icon="ri-user-heart-line"
        title="Subject Selection & Dosing Quantity"
        subtitle="Select subjects and compute total required dosing quantity against unsealed inventory"
      >
        {/* Stock Summary Banner */}
        <Alert
          color={totalQtyNeeded <= netUsableContainerQuantity ? "success" : "warning"}
          className="d-flex align-items-center justify-content-between mb-4"
        >
          <div className="d-flex align-items-center gap-2">
            <i
              className={
                totalQtyNeeded <= netUsableContainerQuantity
                  ? "ri-checkbox-circle-fill fs-4"
                  : "ri-alert-fill fs-4"
              }
            />
            <div>
              <h6 className="alert-heading mb-0 fw-bold">
                Unsealed Container Stock: {netUsableContainerQuantity} Units Available
              </h6>
              <small>
                Selected {selectedContainers.length} container(s). Total Required for Dosing:{" "}
                <strong>{totalQtyNeeded} Units</strong>.
              </small>
            </div>
          </div>
          <Badge
            color={totalQtyNeeded <= netUsableContainerQuantity ? "success" : "warning"}
            className="fs-6 px-3 py-2"
          >
            {totalQtyNeeded <= netUsableContainerQuantity
              ? "Stock Sufficient"
              : "Insufficient Container Stock"}
          </Badge>
        </Alert>

        {/* Subject Selection Pills */}
        <div className="mt-2 mb-4 border rounded p-3 bg-light-subtle">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <Label className="fw-semibold text-dark mb-0">
              Subject No. Selection <span className="text-danger">*</span>
              <Badge color="primary" pill className="ms-2">
                {selectedSubjectNos.length} Selected
              </Badge>
            </Label>
            {subjectList.length > 0 && (
              <div className="d-flex gap-2">
                <Button
                  type="button"
                  color="link"
                  size="sm"
                  className="p-0 text-decoration-none"
                  onClick={() =>
                    setValue("subjectno", subjectList, {
                      shouldValidate: true,
                      shouldDirty: true,
                    })
                  }
                >
                  Select All
                </Button>
                <span className="text-muted">|</span>
                <Button
                  type="button"
                  color="link"
                  size="sm"
                  className="p-0 text-decoration-none text-danger"
                  onClick={() =>
                    setValue("subjectno", [], {
                      shouldValidate: true,
                      shouldDirty: true,
                    })
                  }
                >
                  Clear All
                </Button>
              </div>
            )}
          </div>

          <Controller
            name="subjectno"
            control={control}
            rules={{ required: "At least one Subject No. must be selected" }}
            render={({ field: { value = [], onChange } }) => (
              <div className="d-flex flex-wrap gap-2">
                {subjectList.map((subject: string) => {
                  const isChecked = value.includes(subject);
                  return (
                    <div
                      key={subject}
                      className={`px-3 py-1.5 rounded-pill d-flex align-items-center justify-content-center cursor-pointer user-select-none transition-all ${
                        isChecked
                          ? "bg-primary text-white fw-semibold shadow-sm"
                          : "bg-light-subtle text-dark fw-medium border"
                      } ${styles.subjectPill}`}
                      onClick={() => {
                        if (isChecked) {
                          onChange(value.filter((s: string) => s !== subject));
                        } else {
                          onChange([...value, subject]);
                        }
                      }}
                    >
                      {subject}
                    </div>
                  );
                })}
              </div>
            )}
          />

          {errors.subjectno && (
            <div className="text-danger small mt-2">
              {errors.subjectno.message as string}
            </div>
          )}
        </div>

        {/* Dosing Quantities */}
        <CommonForm
          fields={getDosingQuantityFields()}
          control={control}
          errors={errors}
          columns={3}
        />
      </SectionCard>

      {/* Action Footer for Tab 2 */}
      <div className="d-flex align-items-center justify-content-end gap-2 mt-4 pt-3 border-top">
        <Button type="button" color="dark" outline onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" color="secondary" outline onClick={onPrevious}>
          Previous
        </Button>
        <Button type="submit" color="primary" disabled={isSubmitting}>
          {isSubmitting ? "Saving..." : "Save Dispensing Log"}
        </Button>
      </div>
    </form>
  );
};

export default AddDispensingDetails;
